import { Box, Collapse, Divider, HStack, Skeleton, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { findInstallmentsConfigCustom } from 'services/api.service';
import { InstallmentsConfig } from 'services/types/installmensts.config';
import { maskCurrency } from 'utils/number';
import { calculateInstallments, cardRange, flatNet, type InstallmentMode } from './receivablesCalc';

const MIN_VALUE = 5;

type FeeType = 'FROM_PAYER' | 'FROM_CUSTOMER';

const METHODS: { key: string; label: string; card?: boolean }[] = [
  { key: 'INTEGRATION_PIX', label: 'Pix' },
  { key: 'INTEGRATION_CREDIT_CARD', label: 'Cartão de crédito', card: true },
  { key: 'INTEGRATION_TICKET', label: 'Boleto' },
];

export const ReceivablesSummary = ({
  amount,
  maxInstallments,
  feeType,
}: {
  amount: number;
  maxInstallments: number;
  feeType: FeeType;
}) => {
  if (!amount || amount < MIN_VALUE) {
    return (
      <Text
        fontSize="sm"
        color="gray.600"
        alignSelf="flex-start"
      >
        Informe um valor ≥ {maskCurrency(MIN_VALUE)} para ver quanto você recebe por forma de pagamento.
      </Text>
    );
  }
  return (
    <VStack
      w="100%"
      align="stretch"
      spacing={0}
      borderWidth="1px"
      borderRadius="md"
    >
      {METHODS.map((m, i) => (
        <Box key={m.key}>
          {i > 0 && <Divider />}
          <MethodLine
            method={m.key}
            label={m.label}
            isCard={!!m.card}
            amount={amount}
            feeType={feeType}
            maxInstallments={maxInstallments}
          />
        </Box>
      ))}
    </VStack>
  );
};

const MethodLine = ({
  method,
  label,
  isCard,
  amount,
  feeType,
  maxInstallments,
}: {
  method: string;
  label: string;
  isCard: boolean;
  amount: number;
  feeType: FeeType;
  maxInstallments: number;
}) => {
  const { isOpen, onToggle } = useDisclosure();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['installmentsConfigCustom', method],
    queryFn: () => findInstallmentsConfigCustom(method),
  });

  const mode: InstallmentMode = feeType === 'FROM_CUSTOMER' ? 'FROM_RECEIVER' : 'FROM_PAYER';
  const rows = data && isCard ? calculateInstallments(data, amount, mode, maxInstallments || 0) : [];

  let summary = '—';
  if (data) {
    if (!isCard) {
      summary = `Você recebe ${maskCurrency(flatNet(data, amount).net)}`;
    } else if (feeType === 'FROM_PAYER') {
      summary = `Você recebe ${maskCurrency(amount)} · cliente paga a taxa`;
    } else {
      const range = cardRange(rows);
      if (!range) summary = '—';
      else if (range.low === range.high) summary = `Você recebe ${maskCurrency(range.high)} à vista`;
      else summary = `${maskCurrency(range.low)} (${range.lowInstallments}x) a ${maskCurrency(range.high)} (à vista)`;
    }
  }

  const expandable = !!data && !isLoading && !isError;

  return (
    <Box
      px={4}
      py={3}
    >
      <HStack
        justify="space-between"
        align="center"
        cursor={expandable ? 'pointer' : 'default'}
        onClick={expandable ? onToggle : undefined}
      >
        <Text fontWeight="medium">{label}</Text>
        <HStack spacing={2}>
          {isLoading ? (
            <Skeleton
              h="16px"
              w="140px"
            />
          ) : isError ? (
            <Text
              fontSize="sm"
              color="red.500"
            >
              Não foi possível carregar
            </Text>
          ) : (
            <Text
              fontSize="sm"
              color="gray.600"
              textAlign="right"
            >
              {summary}
            </Text>
          )}
          {expandable && (isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />)}
        </HStack>
      </HStack>

      {expandable && (
        <Collapse in={isOpen}>
          <Box pt={3}>
            {isCard ? (
              <CardDetails
                rows={rows}
                mode={mode}
              />
            ) : (
              <FlatDetails
                config={data as InstallmentsConfig}
                amount={amount}
              />
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const CardDetails = ({ rows, mode }: { rows: ReturnType<typeof calculateInstallments>; mode: InstallmentMode }) => {
  if (rows.length === 0) {
    return (
      <Text
        fontSize="sm"
        color="gray.600"
      >
        Valor muito baixo para gerar parcelas.
      </Text>
    );
  }
  return (
    <VStack
      align="stretch"
      spacing={1}
    >
      {rows.map((r) => (
        <HStack
          key={r.installments}
          justify="space-between"
          fontSize="sm"
        >
          <Text>
            {r.installments}× de {maskCurrency(r.installmentValue)}
          </Text>
          <Text color="gray.600">
            {mode === 'FROM_PAYER'
              ? `cliente paga ${maskCurrency(r.totalValue)}`
              : `você recebe ${maskCurrency(r.netValue)}`}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
};

const FlatDetails = ({ config, amount }: { config: InstallmentsConfig; amount: number }) => {
  const { fee, net } = flatNet(config, amount);
  return (
    <HStack
      justify="space-between"
      fontSize="sm"
    >
      <Text>Bruto {maskCurrency(amount)}</Text>
      <Text color="red.500">− Taxa {maskCurrency(fee)}</Text>
      <Text
        color="green.500"
        fontWeight="semibold"
      >
        = {maskCurrency(net)}
      </Text>
    </HStack>
  );
};
